import React, { useState, useEffect} from 'react';
import './App.css';
import PortalApp from "./PortalApp";
import {SnackbarProvider} from 'notistack';
import 'typeface-roboto';
import 'typeface-montserrat';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import PropTypes from "prop-types";
import {asyncForEach, chunkArray, getErrorMessageFromResponse, hasAccessToWorkspace} from "../common/helper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import {getTheme, prepareTheme} from "./helper";
import CustomSnackbar from "./CustomSnackbar";
import { ConfirmProvider } from "material-ui-confirm";
import { useMsal, useAccount, useIsAuthenticated } from "@azure/msal-react";


const HomeEntra = props => {

    const debug = window.location.pathname.toLowerCase().includes("debug");

    const email = props.user?.email;
    const userRoles = props.user?.roles || [];
    const accessToken = props.user?.accessToken;
    const accessTokenExpiry = props.user?.accessTokenExpiry;

    const theme = getTheme()
    const portalThemeOptions = prepareTheme(theme);
    const portalTheme = createTheme((portalThemeOptions));
    const drawerWidth = theme.drawerWidth;

    const [loadingMsg, setLoadingMsg] = useState("Loading...")
    const [workspaceInfo, setWorkspaceInfo] = useState(null);
    const [userDetails, setUserDetails] = useState(null);
    const [errorDetails, setErrorDetails] = useState(null);
    const [initDone, setInitDone] = useState(false);
    const [configFiles, setConfigFiles] = useState({
        actionsConfig: {},
        dashboardsConfig: [],
        metadataConfig: {},
        optionsConfig: {},
        publicLinksConfig: [],
        templatesConfig: {},
        workflowConfig: {folderActions:[],documentActions:[]},
        workspaces: [],
        generalConfig:{}
    })

    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState("")
    const [snackbarSeverity, setSnackbarSeverity] = useState("info")

    function showSnackbar(message, messageSeverity)  {
        setSnackbarMessage(message)
        setSnackbarSeverity(messageSeverity)
        setOpenSnackbar(true)
    }

    function hideSnackbar()  {
        setSnackbarMessage("")
        setOpenSnackbar(false)
    }

    window.localStorage.setItem('windowUrl', window.location);

    useEffect(() => {

        window.localStorage.setItem('theme',"");

        async function init() {

            console.log('HomeEntra user = ', props.user)

            //get config then set user info
            let config = await getConfigFiles();

            let workspaces = [];

            if (userRoles.length === 0) {
                setErrorDetails("No roles assigned");
            } else {
                //Loop through the Workspaces in config file, if the user has any of the accessroles listed for that workspace then add the Workspace to the list
                config.workspaces.forEach(ws => {
                    if (hasAccessToWorkspace(ws,userRoles)) {
                        workspaces.push(ws);
                    }
                })
                if (workspaces.length === 0) {
                    setErrorDetails("You are not configured to use this application");
                } else {
                    setWorkspaceInfo(workspaces);
                    setUserDetails({
                        isUserLoggedIn: true,
                        userLoggedOut: false,
                        userName: props.user?.userName,
                        userEmail: email,
                        boxId: "",
                        userRoles: userRoles,
                        accessToken: accessToken,
                        accessTokenExpiry: accessTokenExpiry,
                        boxAccessToken: "",
                        caseAccessToken: "",
                        caseUsername: email,
                        otherInfo: "...."
                    })
                }
            }

        }

        init().then (r=>{
            setInitDone(true)
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function getConfigFiles() {

        let config = {
            actionsConfig: {},
            dashboardsConfig: [],
            metadataConfig: {},
            optionsConfig: {},
            publicLinksConfig: [],
            templatesConfig: {},
            workflowConfig: {folderActions:[],documentActions:[]},
            workspaces: [],
        };

        // application config - either retrieve from api or pull from local config
        if (window.REACT_APP_CONFIG_API_REQUIRED === "true" || window.REACT_APP_CONFIG_API_REQUIRED === true) {
            const url = window.REACT_APP_CONFIG_API_BASE_URL;
            const request = {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer " + accessToken
                }
            };
            debug && console.log('get config files from service..url: ', url, 'request:', request);

            fetch(url, request)
                .then(response => response.json())
                .then(data => {
                    debug && console.log('getConfigFiles - response.json', data);

                    config = {
                        workspaces: JSON.parse(data.workspace),
                        actionsConfig: JSON.parse(data.action),
                        metadataConfig: JSON.parse(data.metadata),
                        optionsConfig: JSON.parse(data.metadataoption),
                        publicLinksConfig: JSON.parse(data.link),
                        dashboardsConfig: JSON.parse(data.link),
                    };

                })
                .catch(e => {
                    console.log(e);
                    setErrorDetails("Config Retrieval Error" + e);
                })
        } else {

            debug && console.log ('get config from local config')

            const configFilePaths = {
                workspaces: 'config/configWorkspaces.json',
                actionsConfig: 'config/configActions.json',
                metadataConfig: 'config/configMetadata.json',
                optionsConfig: 'config/configMetadataOptions.json',
                publicLinksConfig: 'config/configPublicLinks.json',
                templatesConfig: 'config/configTemplates.json',
                workflowConfig: 'config/configWorkflow.json',
                dashboardsConfig: 'config/configDashboards.json',
                generalConfig: 'config/configGeneral.json'
            };

            const configPrefix =
                window.location.href.indexOf("file") > -1 || window.location.href.indexOf("folder") > -1 || window.location.href.indexOf("upload") > -1 ? "../../" :
                    window.location.href.indexOf("dashboard") > -1 ? "../" :
                        "";

            debug && console.log ('configPrefix=', configPrefix);

            await asyncForEach(Object.entries(configFilePaths), async (entry) => {

                let url = configPrefix + entry[1];

                await fetch(url)
                    .then(response => {
                        return(response.json())
                    })
                    .then(data => {
                        config[entry[0]] =  data;
                    })
                    .catch(e => {
                        debug && console.log("getConfigFiles exception:" , e, " entry: ", entry);
                    })
            })
        }
        debug && console.log('getConfigFiles done, config =  ', config);
        setConfigFiles(config);
        return config
    }

    const { instance, accounts, inProgress } = useMsal();
    const refreshAuthToken = async () => {

        const refreshCutoff = 10; //mins  //TODO CHANGE TO 10
        const now = new Date() ;
        const expiresAt = new Date(userDetails.accessTokenExpiry)

        console.log ('HomeEntra refreshAuthToken current expiresAt = ', expiresAt)

        const expiresInMinutes = Math.floor((expiresAt - now) / (1000 * 60));
        let tokenUpdated = false;
        const previousAccessToken = userDetails.accessToken;
        let newAccessToken
        let newAccessTokenExpiry

        try {
            if (expiresInMinutes < refreshCutoff) {
                console.log (`refreshAuth0Token - refreshing access token as expires in ${expiresInMinutes} minutes, cutoff = ${refreshCutoff}`)
                const account = instance?.getActiveAccount()

                const scopes = [window.REACT_APP_ENTRA_SCOPES];

                const tokenRequest = {
                    scopes: scopes, //["User.Read"],
                    account: account,
                    forceRefresh: true  //avoiding cached token
                };
                debug && console.log(`HomeEntra refreshAuthToken Auth0 access token expires in ${expiresInMinutes} minutes`);
                // Try silent refresh (uses refresh token behind the scenes)
                await instance.acquireTokenSilent(tokenRequest).then((response) => {
                    console.log('HomeEntra refreshAuthToken  acquireTokenSilent response = ', response)

                    if (response) {

                        newAccessToken = response.accessToken;
                        newAccessTokenExpiry = response.expiresOn;
                        tokenUpdated = newAccessToken !== previousAccessToken

                        if (newAccessToken !== previousAccessToken) {

                           console.log('HomeEntra refreshAuthToken - token has changed, new expiry ' + newAccessTokenExpiry + ' new token = ' + newAccessToken)

                            tokenUpdated = true

                            console.log ('newAccessToken=', newAccessToken)
                            console.log ('newAccessTokenExpiry=', newAccessTokenExpiry)

                            //access token has changed so update userDetails with latest token
                            setUserDetails({
                                ...userDetails,
                                accessToken: newAccessToken,
                                accessTokenExpiry: newAccessTokenExpiry
                            })
                        } else {
                            console.log ('HomeEntra refreshAuthToken - No change to token')
                        }

                    }
                })
            }

            const returnVal = {
                tokenUpdated: tokenUpdated,
                accessToken: newAccessToken,
                accessTokenExpiry: newAccessTokenExpiry
            }

            console.log ('HomeEntra refreshAuthToken now return ', returnVal)
            return (returnVal)

        } catch (error) {
            //show session expiry message and redirect to log in screen
            console.log('HomeEntra refreshAuthToken - Error refreshing token - ', error, ' - Redirect to login screen');
            showSnackbar("Session expired, please log in again", "error")
            await new Promise(resolve => setTimeout(() => {props.logout()}, "1500"));
        }
    }

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={portalTheme}>
                <ConfirmProvider>
                    <div>
                        {/*enqueueSnackbar not available here so use custom snackbar*/}
                        <CustomSnackbar message={snackbarMessage} open={openSnackbar} severity={snackbarSeverity} handleClose={hideSnackbar}/>
                        <SnackbarProvider maxSnack={1} style={{maxWidth: 800}} iconVariant={{error: <i className="material-icons" style={{paddingRight: '10px'}}>error</i>}}>
                            {
                                errorDetails ?
                                    <div style={{textAlign: "center", borderColor: '#ddd #ddd #d8d8d8', boxShadow: 'box-shadow: 0 2px 0 hsla(0,0%,68.6%,.12', width: "50%", height: "50%", overflow: 'visible', margin: 'auto', position: 'absolute', top: '0', left: '0', bottom: '0', right: '0'}}>
                                        <img src={window.location.origin + '/images/authlogo.png'} alt="logo"
                                             style={{display: "block", margin: "0 auto", maxWidth: "200px", maxHeight: "40px"}}/><br/><br/>
                                        <Typography variant="h6">{window.REACT_APP_APP_TITLE}</Typography><br/><br/>
                                        <Typography variant="subtitle1">An error has occurred, please retry.</Typography><br/><br/>
                                        <Typography variant="subtitle2">{errorDetails}</Typography><br/><br/>
                                        <Button variant="contained" color="secondary" onClick={props.logout} style={{width: "200px", marginTop: "20px"}}>Return to login</Button>
                                    </div>
                                    :
                                    initDone && userDetails && workspaceInfo ?
                                        <PortalApp
                                            {...props}
                                            userDetails={userDetails}
                                            logout={props.logout}
                                            triggerRefreshAuthToken = {refreshAuthToken}
                                            configFiles={configFiles}
                                            workspaceConfig={workspaceInfo}
                                            launchDashboard={props.launchDashboard}
                                            drawerWidth={drawerWidth}
                                        /> :
                                        <React.Fragment>
                                            <LinearProgress style={{backgroundColor: '#808080'}}/>
                                            <div style={{color: '#808080', fontSize: '0.75rem', fontWeight: '500', lineHeight: '24px', paddingLeft: '6px'}}>
                                                {loadingMsg}
                                            </div>
                                        </React.Fragment>
                            }
                        </SnackbarProvider>
                    </div>
                </ConfirmProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

HomeEntra.propTypes = {
    user: PropTypes.object.isRequired,
    logout: PropTypes.func.isRequired,
    launchDashboard: PropTypes.bool.isRequired
};
export default HomeEntra;